import React from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { IUpsellOffer } from '@rbi-ctg/menu';
import ActionButton, { ActionButtonVariants } from 'components/action-button';
import Modal, { ModalContent, ModalHeading, ModalSize } from 'components/modal';
import Picture from 'components/picture';
import SanityBlockRenderer, {
  IBlockContentOverrides,
  ISerializers,
  PassthroughComponent,
} from 'components/sanity-block-renderer';

import { useOffersUIContext } from './offers-ui-context';

const UpsellModalHeading = styled(ModalHeading)`
  margin-bottom: 1rem;
`;
const UpsellModalContent = styled(ModalContent)`
  & div.modal-inner {
    display: flex;
    height: 100%;
    justify-content: flex-start;
  }
`;
const UpsellOptionsWrapper = styled.div<{ $numColumns: number }>`
  display: grid;
  ${({ $numColumns }) => Styles.desktop`
    grid-gap: 1rem;
    grid-template-columns: repeat(${$numColumns}, 1fr);
  `}
  margin-bottom: 1rem;
`;

const UpsellOption = styled.div<{ $limitWidth: boolean }>`
  // Reset default button styles
  background: transparent;
  border: none;
  text-align: left;

  display: grid;
  grid-template-columns: 10rem 1fr;
  grid-gap: 0.5rem;
  align-items: center;
  width: 100vw;
  height: 10rem;
  padding: 1rem;
  margn-top: 1rem;

  ${({ $limitWidth }) => Styles.desktop`
    grid-template-columns: unset;
    width: auto;
    height: auto;
    max-height: 18.75rem;
    max-width: ${$limitWidth ? '22.5rem' : 'auto'};
  `}
`;
const TextContent = styled.div`
  height: 5.375rem;
`;

const Title = styled.h3`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  margin: 0;
  font-family: ${Styles.fontFamily.base};
  font-weight: normal;
  font-size: 1rem;
`;
const Description = styled.p`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  margin: 0.5rem 0 0;
  font-size: 0.9rem;
`;
const ButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.7rem;
  padding-bottom: 2rem;
`;

const UpgradeContainer = styled.div<{ $center: boolean }>`
  display: none;

  ${({ $center }) => Styles.desktop`
    display: flex;
    justify-content: ${$center ? 'center' : 'space-between'};
    width: 100%;
    padding: 0 2rem;
    margin-bottom: 1rem;
  `}
`;

const MobileButton = styled(ActionButton)`
  margin: 0 2rem;

  ${Styles.desktop`
    display: none;
  `}
`;

const titleBlockContentOverrides: IBlockContentOverrides = {
  normal: Title,
};

const titleBlockSerializers: ISerializers = {
  marks: {
    em: PassthroughComponent,
  },
  container: PassthroughComponent,
};

const descriptionBlockContentOverrides: IBlockContentOverrides = {
  normal: Description,
};

const descriptionBlockSerializers: ISerializers = {
  container: PassthroughComponent,
};

interface IOfferUpsellModal {
  onDismiss: () => void;
  parentId: string;
  upsellOptions: Array<IUpsellOffer>;
}
const OfferUpsellModal: React.FC<IOfferUpsellModal> = ({ onDismiss, parentId, upsellOptions }) => {
  const { formatMessage } = useIntl();
  const { onSelectOffer } = useOffersUIContext();

  return (
    <Modal
      data-testid="offer-upsell-modal"
      onDismiss={onDismiss}
      size={upsellOptions.length > 1 ? ModalSize.REGULAR : ModalSize.SMALL}
      height={'600px'}
      mParticleEventData={{
        modalAppearanceEventMessage: 'Upsell Offer',
        modalMessage: `Parent Offer ID: ${parentId}`,
      }}
    >
      <UpsellModalContent>
        <UpsellModalHeading id="modal-heading">
          {formatMessage({ id: 'offerUpsellTitle' })}
        </UpsellModalHeading>
        <UpsellOptionsWrapper $numColumns={upsellOptions.length > 1 ? 2 : 1}>
          {upsellOptions.map(offer => (
            <>
              <UpsellOption key={offer._id} $limitWidth={upsellOptions.length > 1}>
                <Picture image={offer.localizedImage?.locale?.app} alt="" objectFitContain={true} />
                <TextContent>
                  <SanityBlockRenderer
                    blockContentOverrides={titleBlockContentOverrides}
                    serializers={titleBlockSerializers}
                    content={offer?.name?.localeRaw}
                  />
                  <SanityBlockRenderer
                    blockContentOverrides={descriptionBlockContentOverrides}
                    serializers={descriptionBlockSerializers}
                    content={offer?.description?.localeRaw}
                  />
                </TextContent>
              </UpsellOption>
              <MobileButton
                key={offer._id}
                variant={ActionButtonVariants.PRIMARY}
                data-testid={`${offer._id}__offer-upsell`}
                onClick={() => {
                  onSelectOffer(offer._id, true);
                }}
              >
                {formatMessage({ id: 'offerUpsellUpgrade' })}
              </MobileButton>
            </>
          ))}
        </UpsellOptionsWrapper>
        <ButtonWrapper>
          <UpgradeContainer $center={upsellOptions.length < 2}>
            {upsellOptions.map(offer => (
              <ActionButton
                key={offer._id}
                variant={ActionButtonVariants.PRIMARY}
                data-testid={`${offer._id}__offer-upsell`}
                onClick={() => {
                  onSelectOffer(offer._id, true);
                }}
              >
                {formatMessage({ id: 'offerUpsellUpgrade' })}
              </ActionButton>
            ))}
          </UpgradeContainer>
          <ActionButton
            variant={ActionButtonVariants.OUTLINE}
            data-testid="offer-upsell-discard"
            onClick={onDismiss}
          >
            {formatMessage({ id: 'offerUpsellDiscard' })}
          </ActionButton>
        </ButtonWrapper>
      </UpsellModalContent>
    </Modal>
  );
};

export default OfferUpsellModal;
